import { apiClient } from './client';
import type {
  Car,
  CarImage,
  CarFilters,
  PaginationParams,
  SortParams,
  PaginatedResponse,
  ApiResponse,
  FilterOptions,
} from '@/types';

export interface GetCarsParams extends CarFilters, Partial<PaginationParams>, Partial<SortParams> {}

export async function getCars(params: GetCarsParams = {}): Promise<PaginatedResponse<Car>> {
  // Clean up undefined values and convert arrays to comma-separated strings
  const cleanParams = Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => {
        // Convert arrays to comma-separated strings for the API
        if (Array.isArray(value)) {
          return [key, value.join(',')];
        }
        return [key, value];
      })
  );

  const response = await apiClient.get<PaginatedResponse<Car>>('/cars', { params: cleanParams });
  return response.data;
}

export async function getCarById(id: string): Promise<ApiResponse<Car>> {
  const response = await apiClient.get<ApiResponse<Car>>(`/cars/${id}`);
  return response.data;
}

/**
 * Featured cars for the Home hero/marquee (newest first). Thin convenience over
 * `getCars` with the `featured` filter the backend already supports.
 */
export async function getFeaturedCars(limit = 10): Promise<Car[]> {
  try {
    const response = await getCars({ featured: true, limit, sortBy: 'createdAt', sortOrder: 'desc' });
    return response.data ?? [];
  } catch {
    return [];
  }
}

export async function getFilterOptions(): Promise<ApiResponse<FilterOptions>> {
  const response = await apiClient.get<ApiResponse<FilterOptions>>('/cars/options');
  return response.data;
}

export async function getModelsByMake(make: string): Promise<ApiResponse<string[]>> {
  const response = await apiClient.get<ApiResponse<string[]>>('/cars/models', {
    params: { make },
  });
  return response.data;
}

// Admin API - fetches from MSSQL (not JSON test data)
export async function getAdminCars(params: GetCarsParams = {}): Promise<PaginatedResponse<Car>> {
  const cleanParams = Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return [key, value.join(',')];
        }
        return [key, value];
      })
  );

  const response = await apiClient.get<PaginatedResponse<Car>>('/admin/cars', { params: cleanParams });
  return response.data;
}

export interface CreateCarData {
  make: string;
  model: string;
  year: number;
  price: number;
  mileageKm: number;
  fuelType: string;
  transmission: string;
  bodyType: string;
  color: string;
  city: string;
  region: string;
  addressLine1?: string;
  neighborhood?: string;
  latitude?: number;
  longitude?: number;
  showExactLocation?: boolean;
  description?: string;
  status?: string;
  vin?: string;
  chassisNumber?: string;
  licensePlate?: string;
  firstRegistrationDate?: string;
  condition?: string;
  plugInHybrid?: boolean;
  images?: Array<{
    url: string;
    altText: string;
    isPrimary?: boolean;
  }>;
}

export async function createCar(data: CreateCarData): Promise<ApiResponse<Car>> {
  const response = await apiClient.post<ApiResponse<Car>>('/admin/cars', data);
  return response.data;
}

export async function updateCar(id: string, data: Partial<CreateCarData>): Promise<ApiResponse<Car>> {
  const response = await apiClient.put<ApiResponse<Car>>(`/admin/cars/${id}`, data);
  return response.data;
}

export async function deleteCar(id: string): Promise<ApiResponse<void>> {
  const response = await apiClient.delete<ApiResponse<void>>(`/admin/cars/${id}`);
  return response.data;
}

export async function addCarImage(
  carId: string,
  data: { url: string; altText: string; isPrimary?: boolean }
): Promise<ApiResponse<unknown>> {
  const response = await apiClient.post(`/admin/cars/${carId}/images`, data);
  return response.data;
}

export async function deleteCarImage(carId: string, imageId: string): Promise<ApiResponse<void>> {
  const response = await apiClient.delete<ApiResponse<void>>(`/admin/cars/${carId}/images/${imageId}`);
  return response.data;
}

/**
 * Upload image files for a car (multipart/form-data).
 */
export async function uploadCarImages(carId: string, files: File[]): Promise<ApiResponse<CarImage[]>> {
  const formData = new FormData();
  files.forEach((file) => formData.append('images', file));

  const response = await apiClient.post<ApiResponse<CarImage[]>>(
    `/admin/cars/${carId}/images/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

/**
 * Reorder images for a car.
 */
export async function reorderCarImages(
  carId: string,
  ordering: Array<{ id: string; sortOrder: number }>
): Promise<ApiResponse<CarImage[]>> {
  const response = await apiClient.put<ApiResponse<CarImage[]>>(
    `/admin/cars/${carId}/images/reorder`,
    { ordering }
  );
  return response.data;
}

/**
 * Set primary image for a car.
 */
export async function setPrimaryCarImage(
  carId: string,
  imageId: string
): Promise<ApiResponse<void>> {
  const response = await apiClient.put<ApiResponse<void>>(
    `/admin/cars/${carId}/images/primary`,
    { imageId }
  );
  return response.data;
}

export async function updateCarImageCrop(
  carId: string,
  imageId: string,
  crop: Partial<CarImage['crop']>
): Promise<ApiResponse<CarImage>> {
  const response = await apiClient.patch<ApiResponse<CarImage>>(
    `/admin/cars/${carId}/images/${imageId}/crop`,
    crop
  );
  return response.data;
}

export async function getSimilarCars(carId: string, limit: number = 6): Promise<ApiResponse<Car[]>> {
  const response = await apiClient.get<ApiResponse<Car[]>>(`/cars/${carId}/similar`, {
    params: { limit },
  });
  return response.data;
}

// Admin: approve a pending listing (set status to active)
export async function approveCar(id: string): Promise<ApiResponse<Car>> {
  const response = await apiClient.patch<ApiResponse<Car>>(`/admin/cars/${id}/approve`);
  return response.data;
}

// Bulk admin operations
export interface BulkDeleteResponse {
  success: boolean;
  message: string;
  deletedCount: number;
  deletedFilesCount: number;
  warnings?: string[];
}

export interface BulkStatusResponse {
  success: boolean;
  message: string;
  updatedCount: number;
}

export interface DeleteCarResponse {
  success: boolean;
  message: string;
  deletedFilesCount?: number;
  warnings?: string[];
}

export async function bulkDeleteCars(ids: string[]): Promise<BulkDeleteResponse> {
  const response = await apiClient.post<BulkDeleteResponse>('/admin/cars/bulk-delete', { ids });
  return response.data;
}

export async function bulkUpdateStatus(ids: string[], status: string): Promise<BulkStatusResponse> {
  const response = await apiClient.post<BulkStatusResponse>('/admin/cars/bulk-status', { ids, status });
  return response.data;
}
